/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';

import Link from '../Link';
import { px2rem } from '../../utilities/UnitHelper';

const StyledNav = styled.nav`
  box-sizing: border-box;

  > ul {
    display: flex;
    list-style: none;
    height: 3.5rem;
    margin: 0;
    padding: 0;

    > li {
      height: 100%;
      position: relative;
      margin: 0 ${px2rem(12)};

      > a {
        ${(props) => props.theme.typography.body}
        color: ${(props) => props.theme.colors.grayscale.black};
        align-items: center;
        display: flex;
        height: 100%;
        text-decoration: none;

        ::after {
          background-color: ${(props) => props.theme.colors.yellow.primary};
          content: '';
          height: 0.15em;
          opacity: 0;
          position: absolute;
          top: 2.5rem;
          transform-origin: center;
          transform: scale(0);
          transition:
            opacity 100ms,
            transform 300ms;
          width: 100%;
        }

        &:hover::after,
        &:focus::after {
          opacity: 1;
          transform: scale(1);
        }
      }

      &:hover {
        ul {
          display: block;
        }
      }

      &.active a::after {
        opacity: 1;
        transform: scale(1);
      }
    }
  }
`;

const StyledSubNav = styled.ul`
  background-color: ${(props) => props.theme.colors.grayscale.white};
  border-radius: 4px;
  border: 1px solid #ededed;
  box-shadow: 1px 1px 4px 1px rgb(0 0 0 / 15%);
  display: none;
  margin: 0;
  padding: 0;
  position: absolute;
  top: 3.25rem;
  z-index: ${(props) => props.theme.zIndex.subNav};

  li {
    list-style: none;
    margin: 0;
    padding: 0;

    &.active a {
      background: ${(props) => props.theme.colors.grayscale.black};
      color: ${(props) => props.theme.colors.grayscale.white};
    }
  }

  a {
    ${(props) => props.theme.typography.bodySmall}
    display: block;
    padding: 0.75rem 1rem;

    &:hover {
      background: ${(props) => props.theme.colors.grayscale.black};
      color: ${(props) => props.theme.colors.grayscale.white};
    }
  }
`;

StyledSubNav.displayName = 'SubNav';

/**
 * Nav that contains a list of links to be displayed
 * and handles the active state via URLs.
 */
function Nav({ items, initialUrl, onClick }) {
  const [activeUrl, setActiveUrl] = useState(initialUrl);

  useEffect(() => {
    setActiveUrl(initialUrl);
  }, [initialUrl]);

  /**
   * Provides a click event handler for the nav items.
   *
   * @param {Object} itemName - the clicked item name
   */
  const clickHandler = (itemName) => (event) => {
    if (typeof onClick === 'function') {
      event.preventDefault();
      onClick(event, itemName);
    }
  };

  /**
   * Returns true if the given nav item is currently active
   *
   * @param {Object} item nav item
   * @returns {Boolean}
   */
  function isActiveNavItem(item) {
    return (
      item.url === activeUrl ||
      (item.subNav &&
        item.subNav.some((subNavItem) => subNavItem.url === activeUrl)) ||
      (item.matchingPatterns &&
        item.matchingPatterns.some((regex) => regex.test(activeUrl))) ||
      (item.subNav &&
        item.subNav.some(
          (subNavItem) =>
            subNavItem.matchingPatterns &&
            subNavItem.matchingPatterns.some((regex) => regex.test(activeUrl))
        ))
    );
  }

  /**
   * List items array reducer
   *
   * @param {Array} listItems list item elements array
   * @param {Object} item item object
   * @param {string} item.name item name
   * @param {string} item.url item url
   * @param {Function} [item.linkWrapper] link wrapper component
   * @returns {Array} listItems
   */
  function listItemReducer(listItems, item, idx) {
    if (typeof item.name === 'string' && typeof item.url === 'string') {
      const isActive = isActiveNavItem(item);
      listItems.push(
        <li className={isActive ? 'active' : ''} key={item.url}>
          <Link
            aria-label={isActive ? `${item.name} (currently active)` : null}
            href={item.url}
            data-subnav={item.subNav ? idx : false}
            LinkWrapper={item.linkWrapper}
            onClick={clickHandler(item.name)}
            id={item.id}
            rel={item.rel ?? undefined}
          >
            {item.name}
          </Link>
          {item.subNav && (
            <StyledSubNav>
              {item.subNav.map((subNavItem) => {
                const isActiveSubNavItem = !!isActiveNavItem(subNavItem);
                return (
                  <li
                    key={subNavItem.id}
                    className={isActiveSubNavItem ? 'active' : null}
                  >
                    <Link
                      id={`subnav-${subNavItem.id}`}
                      aria-label={
                        isActiveSubNavItem
                          ? `${subNavItem.name} (currently active)`
                          : null
                      }
                      href={subNavItem.url}
                      LinkWrapper={subNavItem.linkWrapper}
                      onClick={clickHandler(subNavItem.name)}
                    >
                      {subNavItem.name}
                    </Link>
                  </li>
                );
              })}
            </StyledSubNav>
          )}
        </li>
      );
    } else {
      console.error('Invalid Nav item:', item);
    }

    return listItems;
  }

  const listItems = items.reduce(listItemReducer, []);

  return (
    <StyledNav>
      <ul aria-label="Primary Navigation">{listItems}</ul>
    </StyledNav>
  );
}

/**
 * propTypes for this component
 */
Nav.propTypes = {
  /** Initial active url value */
  initialUrl: PropTypes.string,
  /** Nav items */
  items: PropTypes.arrayOf(
    PropTypes.shape({
      linkWrapper: PropTypes.func,
      name: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
      /** Array of regular expressions to match active URL */
      matchingPatterns: PropTypes.arrayOf(PropTypes.instanceOf(RegExp)),
      /** Array of subnav items */
      subNav: PropTypes.arrayOf(
        PropTypes.shape({
          linkWrapper: PropTypes.func,
          name: PropTypes.string.isRequired,
          url: PropTypes.string.isRequired,
          /** Array of regular expressions to match active URL */
          matchingPatterns: PropTypes.arrayOf(PropTypes.instanceOf(RegExp)),
        })
      ),
    })
  ).isRequired,
  /** Gets called with event and item name in the internal onClick before changing active URL */
  onClick: PropTypes.func,
};

/**
 * Default prop types
 */
Nav.defaultProps = {
  initialUrl: '',
  onClick: null,
};

/* @component */
export default Nav;
